import { forwardRef } from 'react';
import type { DescriptionListProps } from './DescriptionList.types';

/**
 * DescriptionList del sistema de diseño Siesa
 *
 * Componente para mostrar información estructurada en formato clave-valor (term/details).
 * Layout de dos columnas con separador inferior entre items.
 *
 * Mejores prácticas implementadas:
 * - Orden de modificadores: {responsive}:{dark}:{state}:{utility}
 * - Dark mode con estrategia 'class' (darkMode: 'class')
 * - Tokens de color consistentes con la documentación
 * - Type safety con TypeScript estricto
 * - Layout flexible y responsive
 * - Tipografía del sistema (Paragraph/Small - 14px)
 *
 * @see docs/colors.md - Sistema de colores (content-primary, content-secondary)
 * @see docs/typography.md - Sistema tipográfico (Paragraph/Small)
 * @see docs/spacing.md - Sistema de espaciado (padding, gaps)
 * @see https://www.figma.com/design/5XNqf2YTxvwemxwo1LMQ6j/Siesa-UI-Kit?node-id=4103-8449 - Especificaciones Figma
 *
 * @example
 * ```tsx
 * // Item básico
 * <DescriptionList
 *   term="Customer"
 *   details="Jhonatan Diaz"
 * />
 *
 * // Lista completa
 * <div>
 *   <DescriptionList term="Event" details="Bear Hug: Live in Concert" />
 *   <DescriptionList term="Amount" details="$120,000 COP" />
 *   <DescriptionList term="Fee" details="$20,000 COP" />
 * </div>
 * ```
 */
export const DescriptionList = forwardRef<HTMLDivElement, DescriptionListProps>(
  (
    {
      term,
      details,
      className = '',
      ...props
    },
    ref
  ) => {
    // ===== CLASES BASE =====
    const baseClasses = `
      flex
      flex-wrap
      gap-1
      items-start
      py-2.5
      border-b
      border-border-secondary
      dark:border-gray-700
    `;

    // ===== CLASES DE COLUMNA =====
    // Cada columna ocupa el 50% del espacio disponible (flex-1)
    // con un ancho mínimo de 180px para mantener legibilidad
    const columnClasses = 'flex-1 min-w-[180px]';

    // ===== COMBINAR CLASES =====
    const finalClasses = [baseClasses, className]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <div ref={ref} className={finalClasses} {...props}>
        {/* Term Column (izquierda) */}
        {/* Light: content-secondary (#a1a1aa) */}
        {/* Dark: gray-400 (#9ca3af - gris medio claro para mantener contraste) */}
        <div className={columnClasses}>
          <p className="text-sm leading-5 text-content-secondary dark:text-gray-400 font-normal">
            {term}
          </p>
        </div>

        {/* Details Column (derecha) */}
        {/* Light: content-primary (#18181b) */}
        {/* Dark: dark-content-primary (#ffffff) */}
        <div className={columnClasses}>
          <p className="text-sm leading-5 text-content-primary dark:text-dark-content-primary font-normal">
            {details}
          </p>
        </div>
      </div>
    );
  }
);

DescriptionList.displayName = 'DescriptionList';
